/** A replayable subscription request, never a trading/control command. */
export interface SubscriptionIntent {
  type: 'subscribe' | 'unsubscribe';
  topic?: string;
  channels?: string[];
  symbols?: string[];
  events?: string[];
  payload?: Record<string, unknown>;
  [key: string]: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const selectionsOf = (message: Record<string, unknown>): unknown[] => {
  const payload = isRecord(message['payload']) ? message['payload'] : undefined;
  return [message['channels'], message['symbols'], message['events'], payload?.['channels'], payload?.['symbols']];
};

/**
 * Compact supported subscription routes into desired selections while offline.
 * The complete envelope survives replay, including top-level performance channels,
 * risk events, market symbols and nested trading selections. Commands are rejected.
 */
export function updateSubscriptionIntents(previous: readonly SubscriptionIntent[], message: unknown): SubscriptionIntent[] {
  if (!isRecord(message) || (message['type'] !== 'subscribe' && message['type'] !== 'unsubscribe')) { return [...previous]; }
  const payload = isRecord(message['payload']) ? message['payload'] : undefined;
  const selections = selectionsOf(message);
  if (selections.some(value => value !== undefined && (!Array.isArray(value) || value.some(item => typeof item !== 'string' || !item)))) {
    return [...previous];
  }
  const choices = selections.map(value => Array.isArray(value) && value.length ? [...new Set(value)] : [undefined]);
  if (choices.reduce((size, choice) => size * choice.length, 1) > 4096) { return [...previous]; }
  let routes: (string | undefined)[][] = [[]];
  for (const choice of choices) { routes = routes.flatMap(route => choice.map(value => [...route, value])); }
  let result = [...previous];
  for (const route of routes) {
    const intent: SubscriptionIntent = { ...message, type: 'subscribe' };
    if (payload) { intent.payload = { ...payload }; }
    for (const [index, field] of ['channels', 'symbols', 'events', 'channels', 'symbols'].entries()) {
      const value = route[index];
      if (value !== undefined) {
        if (index < 3) { intent[field] = [value]; }
        else if (intent.payload) { intent.payload[field] = [value]; }
      }
    }
    const sameRoute = (existing: SubscriptionIntent): boolean =>
      (existing.topic ?? null) === (intent.topic ?? null) &&
      (existing.payload?.['topic'] ?? null) === (intent.payload?.['topic'] ?? null);
    const hadBroaderSelection = result.some(existing => sameRoute(existing) && existing.type === 'subscribe' &&
      selectionsOf(existing).some(value => Array.isArray(value) && (!value.length || value.includes('all'))));
    result = result.filter(existing => {
      if (!sameRoute(existing)) { return true; }
      const existingSelections = selectionsOf(existing).map(value => Array.isArray(value) ? value[0] : undefined);
      return !route.every((selection, index) => selection === existingSelections[index] ||
        (message['type'] === 'unsubscribe' && (selection === undefined || selection === 'all')));
    });
    if (message['type'] === 'subscribe') { result.push(intent); }
    else if (hadBroaderSelection && route.some(value => value !== undefined && value !== 'all')) {
      // Retain an explicit exclusion after a server-owned "all" subscription.
      result.push({ ...intent, type: 'unsubscribe' });
    }
  }
  return result;
}
